import { useCallback, useEffect, useMemo, useState } from 'react'
import { getShipmentInternalLabelLaser } from '../services/ShipmentServices'

export type ShipmentLabelFormat = 'html' | 'pdf' | 'pdf-letter'

export const SHIPMENT_LABEL_FORMATS: ShipmentLabelFormat[] = [
  'html',
  'pdf',
  'pdf-letter',
]

/**
 * Normaliza lo que venga de la URL o de un select: acepta mayúsculas y guion
 * bajo (`PDF_LETTER` → `pdf-letter`). Devuelve null si no es un formato válido,
 * para poder mostrar el error en pantalla en vez de llamar al servicio a ciegas.
 */
export const parseShipmentLabelFormat = (
  value: unknown
): ShipmentLabelFormat | null => {
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase().replace(/_/g, '-')
  return SHIPMENT_LABEL_FORMATS.includes(normalized as ShipmentLabelFormat)
    ? (normalized as ShipmentLabelFormat)
    : null
}

interface ShipmentLabelState {
  loading: boolean
  /** Mensaje de error listo para mostrar (null si la etiqueta se generó bien). */
  error: string | null
  /** Contenido HTML crudo devuelto por el backend (formato HTML). */
  htmlContent: string | null
  /** Blob URL del PDF ya decodificado (formatos PDF y PDF-LETTER). */
  pdfUrl: string | null
  /** `message` que devolvió el servicio, haya fallado o no. */
  message: string | null
  /** `code` que devolvió el servicio, haya fallado o no. */
  code: string | null
  /** `labelFormat` que confirmó el backend (puede diferir del solicitado). */
  labelFormat: 'HTML' | 'PDF' | 'PDF-LETTER' | null
  /**
   * El pedido murió por tiempo (no hubo respuesta del servidor).
   *
   * Se expone aparte de `error` porque el llamador REACCIONA a esto: al detectarlo,
   * vuelve a pedir las etiquetas en grupos más chicos en vez de solo mostrar el texto.
   */
  timedOut: boolean
}

const EMPTY_STATE: ShipmentLabelState = {
  loading: false,
  error: null,
  htmlContent: null,
  pdfUrl: null,
  message: null,
  code: null,
  labelFormat: null,
  timedOut: false,
}

/**
 * El backend entrega el PDF como base64 dentro de `stringLabel`; lo pasamos a
 * Blob URL para poder mostrarlo en un iframe e imprimirlo.
 */
const base64ToPdfUrl = (base64: string): string => {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return window.URL.createObjectURL(
    new Blob([bytes], { type: 'application/pdf' })
  )
}

/**
 * Pide la etiqueta a Django (`POST /label`) para un lote de guías y deja el
 * resultado listo para renderizar. Centraliza el decodificado base64→Blob y la
 * liberación del object URL, que antes vivía duplicado en cada modal.
 */
export const useShipmentLabel = (
  identifications: Array<string | number>,
  format: ShipmentLabelFormat = 'pdf',
  enabled = true
): ShipmentLabelState & { reload: () => void } => {
  const [state, setState] = useState<ShipmentLabelState>(EMPTY_STATE)
  const [reloadToken, setReloadToken] = useState(0)

  // Las guías viajan al efecto como clave estable ("A,B,C") para que no se
  // redispare cuando el caller arma el array inline en cada render.
  const identificationsKey = useMemo(
    () =>
      identifications
        .map((value) => String(value ?? '').trim())
        .filter((value) => value.length > 0)
        .join(','),
    [identifications]
  )

  useEffect(() => {
    if (!enabled || !identificationsKey) {
      setState(EMPTY_STATE)
      return
    }

    let objectUrl: string | null = null
    let active = true

    setState({ ...EMPTY_STATE, loading: true })

    const fetchLabel = async () => {
      const result = await getShipmentInternalLabelLaser(
        identificationsKey.split(','),
        format
      )

      if (!active) return

      if (!result.ok || !result.content) {
        setState({
          ...EMPTY_STATE,
          error: result.message ?? 'No se pudo generar la etiqueta.',
          message: result.message ?? null,
          code: result.code ?? null,
          timedOut: !!result.timedOut,
        })
        return
      }

      const next: ShipmentLabelState = {
        ...EMPTY_STATE,
        labelFormat: result.labelFormat ?? null,
        message: result.message ?? null,
        code: result.code ?? null,
      }

      if (result.labelFormat === 'HTML') {
        next.htmlContent = result.content
      } else {
        try {
          objectUrl = base64ToPdfUrl(result.content)
          next.pdfUrl = objectUrl
        } catch {
          next.error = 'El servicio devolvió un PDF que no se pudo decodificar.'
        }
      }

      setState(next)
    }

    fetchLabel().catch((error: any) => {
      if (!active) return
      setState({
        ...EMPTY_STATE,
        error: error?.message ?? 'No se pudo generar la etiqueta.',
      })
    })

    return () => {
      active = false
      if (objectUrl) window.URL.revokeObjectURL(objectUrl)
    }
  }, [identificationsKey, format, enabled, reloadToken])

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  return { ...state, reload }
}
